import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Routes, Route } from 'react-router-dom';
import Coin from './Coin/Coin';
import Coins from '../components/CryptoCurrencies/Coins';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import ErrorCard from '../ui/ErrorCard';
import '../ui/DailyPrices.css';

const COIN_DATA_KEY = 'cryptoData';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

type CoinType = {
  id: string;
  name: string;
  symbol: string;
  image: string;
  current_price: number;
  market_cap: number;
  market_cap_change_percentage_24h: number;
};

const fetchCryptoData = async (): Promise<CoinType[]> => {
  const url = 'https://api.coingecko.com/api/v3/coins/markets';
  const params = {
    vs_currency: 'usd',
    order: 'market_cap_desc',
    per_page: 200,
    page: 1,
    sparkline: false
  };
  const { data } = await axios.get(url, { params });
  return data;
};

function DailyPrices() {
  const [coins, setCoins] = useState<CoinType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery] = useState('');

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      setError(null);
      try {
        const cached = sessionStorage.getItem(COIN_DATA_KEY);
        const cachedTime = sessionStorage.getItem(`${COIN_DATA_KEY}_timestamp`);
        const now = Date.now();

        if (cached && cachedTime && now - Number(cachedTime) < CACHE_DURATION_MS) {
          const parsed = JSON.parse(cached);
          setCoins(parsed);
        } else {
          const freshData = await fetchCryptoData();
          sessionStorage.setItem(COIN_DATA_KEY, JSON.stringify(freshData));
          sessionStorage.setItem(`${COIN_DATA_KEY}_timestamp`, now.toString());
          setCoins(freshData);
        }
      } catch (err) {
        console.error(err);
        setError('Oops! Failed to load cryptocurrencies. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    getData();
  }, []);



  const filteredCoins = useMemo(() => {
    return coins.filter((coin) =>
      coin.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, coins]);

  const totalMarketCap = useMemo(() => {
    return coins.reduce((acc, coin) => acc + coin.market_cap, 0);
  }, [coins]);

  const marketCapChange =
    coins.length > 0 ? coins[0].market_cap_change_percentage_24h : 0;

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorCard message={error} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <header className="text-center mb-8 space-y-2">
        <h1 className="text-4xl font-bold tracking-tight">Top Cryptocurrencies</h1>
        <p
          className={`text-lg font-medium ${
            marketCapChange >= 0 ? 'text-green-600' : 'text-red-500'
          }`}
        >
          Global Market Cap: ${totalMarketCap.toLocaleString()} (
          {marketCapChange.toFixed(2)}% in 24h)
        </p>
        <p className="text-gray-600">
          Swap, Track, and Convert – Your one-stop crypto hub
        </p>

      </header>

      <Routes>
        <Route path="/" element={<Coins coins={filteredCoins} />} />
        <Route path="/coins/:coinId" element={<Coin />} />
      </Routes>
    </div>
  );
}

export default DailyPrices;











